import classNames from 'classnames';
import { autobind } from 'core-decorators';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { _ } from '/@/locale';
import { formatDate } from '/@/utils';
import styles from './styles.module.css';

@autobind
export default class SoyCycle extends Component {
    static propTypes = {
        plantingDate: PropTypes.string.isRequired,
        products: PropTypes.array.isRequired,
    };

    getReducedLength(maxRemainder) {
        // Returns 30 (days) or maxRemainder, whichever is higher
        if (maxRemainder > 30) return maxRemainder;
        return 30;
    }

    renderSafra(product, safra, cycleEndDate) {
        if (product.cycle <= safra) {
            const widthPercentage = (product.cycle * 100) / safra;
            return (
                <div className={styles.product} style={{ width: `${widthPercentage}%` }}>
                    <div
                        className={classNames(
                            styles.bar,
                            styles[`theme-${product.product.brand.theme}`],
                            styles.rounded
                        )}
                    >
                        <div>{product.product.name}</div>
                    </div>
                    <div className={styles.captionContainer} style={{ left: `${widthPercentage - 10}%` }}>
                        <div className={classNames(styles.caption, styles.captionRight)}>
                            {formatDate(cycleEndDate.toDate(), { format: 'long' })}
                        </div>
                    </div>
                    <div className={styles.cycleReference}>
                        <div className={styles.horizontalLine} />
                        <div className={styles.cycleReferenceText}>
                            {product.cycle} {_('días')}
                        </div>
                        <div className={styles.horizontalLine} />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.product}>
                <div className={classNames(styles.bar, styles[`theme-${product.product.brand.theme}`])}>
                    <div>{product.product.name}</div>
                </div>
                <div className={styles.cycleReference}>
                    <div className={styles.horizontalLine} />
                    <div className={styles.cycleReferenceText}>
                        {product.cycle} {_('días')}
                    </div>
                    <div className={styles.horizontalLine} />
                </div>
            </div>
        );
    }

    renderExcellent(product, safra, high, excellent, cycleEndDate) {
        // Calculates remaining days for harvest after High period
        if (product.cycle > safra && product.cycle <= safra + high + excellent) {
            const remainder = product.cycle - safra;
            const widthPercentage = (remainder * 100) / (excellent + high);
            return (
                <div className={styles.product} style={{ width: `${widthPercentage}%` }}>
                    <div
                        className={classNames(
                            styles.bar,
                            styles[`theme-${product.product.brand.theme}`],
                            styles.rounded
                        )}
                    />
                    <div
                        className={styles.captionContainer}
                        style={
                            widthPercentage > 50
                                ? { right: `${110 - widthPercentage}%` }
                                : { left: `${widthPercentage - 10}%` }
                        }
                    >
                        <div
                            className={classNames(
                                styles.caption,
                                widthPercentage > 50 ? styles.captionLeft : styles.captionRight
                            )}
                        >
                            {formatDate(cycleEndDate.toDate(), { format: 'long' })}
                        </div>
                    </div>
                    <div className={styles.cycleReference}>
                        <div className={styles.horizontalLine} />
                    </div>
                </div>
            );
        }

        if (product.cycle > safra + high + excellent) {
            return (
                <div className={styles.product}>
                    <div className={classNames(styles.bar, styles[`theme-${product.product.brand.theme}`])} />
                    <div className={styles.cycleReference}>
                        <div className={styles.horizontalLine} />
                    </div>
                </div>
            );
        }
    }

    renderReduced(products, product, safra, high, excellent, cycleEndDate) {
        if (product.cycle > safra + high + excellent) {
            // Calculates remaining days for harvest after Excellent period
            const remainder = product.cycle - (safra + high + excellent);
            // Calculates remaining days (for harvest after Excellent period) of the product with the longest cycle
            const maxRemainder = products[products.length - 1].cycle - (safra + high + excellent);
            const widthPercentage = (remainder * 80) / this.getReducedLength(maxRemainder);

            return (
                <div className={styles.product} style={{ width: `${widthPercentage}%` }}>
                    <div
                        className={classNames(
                            styles.bar,
                            styles[`theme-${product.product.brand.theme}`],
                            styles.rounded
                        )}
                    />
                    <div className={styles.captionContainer} style={{ right: `${110 - widthPercentage}%` }}>
                        <div className={classNames(styles.caption, styles.captionLeft)}>
                            {formatDate(cycleEndDate.toDate(), { format: 'long' })}
                        </div>
                    </div>
                    <div className={styles.cycleReference}>
                        <div className={styles.horizontalLine} />
                    </div>
                </div>
            );
        }
    }

    render() {
        // Sorts products by cycle in ascending order
        const products = this.props.products.filter((product) => product.cycle).sort((a, b) => a.cycle - b.cycle);
        if (products.length === 0) return null;

        const plantingDate = moment(this.props.plantingDate);

        // Gets start dates for High (safrinha), Excellent and Reduced periods
        const { offSeasonStart, excellentStart, reducedStart } = products[0];

        // 'safra': the period between the planting date and the start of safrinha
        const safra = moment(offSeasonStart).diff(plantingDate, 'days');
        // 'high': the period between the start of safrinha and the start of the 'excellent' period
        const high = moment(excellentStart).diff(offSeasonStart, 'days');
        // 'excellent': the period between the start of the 'excellent' period and the start of the 'reduced' period
        const excellent = moment(reducedStart).diff(excellentStart, 'days');

        return (
            <div>
                <div className={styles.chart}>
                    <div className={styles.header}>
                        <div />
                        <div>{_('Mayor disponibilidad de agua')}</div>
                        <div>{_('Menor disponibilidad de agua')}</div>
                    </div>
                    {products.map((product) => {
                        // Calculates end of cycle date
                        const cycleEndDate = moment(plantingDate).add(product.cycle, 'days');
                        return (
                            <div key={product.product.name} className={styles.productContainer}>
                                <div className={styles.column}>{this.renderSafra(product, safra, cycleEndDate)}</div>
                                <div className={styles.column}>
                                    {this.renderExcellent(product, safra, high, excellent, cycleEndDate)}
                                </div>
                                <div className={styles.column}>
                                    {this.renderReduced(products, product, safra, high, excellent, cycleEndDate)}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className={styles.dateReference}>
                    <div>
                        {formatDate(plantingDate.toDate(), { format: 'long' })}
                        <div>{_('Siembra de soja')}</div>
                    </div>
                    <div>{formatDate(moment(offSeasonStart).toDate(), { format: 'long' })}</div>
                    <div>{formatDate(moment(reducedStart).toDate(), { format: 'long' })}</div>
                </div>
            </div>
        );
    }
}
